// Déchets et propreté (M13, lot L3-B, D-044) : secteurs de collecte avec emprise, points de collecte,
// véhicules, prestataires, tournées planifiées par secteur et jour de la semaine, réalisations
// (réalisée, partielle, non réalisée avec motif) saisies au bureau ou reçues par la synchronisation
// terrain (section 4 du cadrage L3 : opération idempotente par id_client, conflit sur version_attendue),
// coûts par tournée ou par prestataire, filtres et couches GeoJSON de la carte. Les dépôts sauvages sont
// des signalements de catégorie depot_sauvage (table de la partie A).
import { z } from 'zod';
import { schemaBbox } from './dossiers';
import { schemaPolygoneGeoJson } from './marches-domaine-public';
import { schemaPosition } from './marches-structure';
import { lundiDeLaSemaine } from './rendez-vous-agendas';

const uuid = z.string().uuid('Identifiant invalide');
const texteCourt = (max: number) => z.string().trim().min(1).max(max);

export const schemaCodeProprete = z.string().trim().regex(/^[A-Za-z0-9][A-Za-z0-9_.-]{0,29}$/, 'Code de 1 à 30 caractères (lettres, chiffres, point, tiret, souligné)');

export const TYPES_POINT_COLLECTE = { bac: 'Bac roulant', conteneur: 'Conteneur', point_apport: 'Point d\'apport volontaire' } as const;
export type TypePointCollecte = keyof typeof TYPES_POINT_COLLECTE;
export const CODES_TYPES_POINT = Object.keys(TYPES_POINT_COLLECTE) as [TypePointCollecte, ...TypePointCollecte[]];

export const OPERATEURS_TOURNEE = { commune: 'Régie communale', prestataire: 'Prestataire' } as const;
export type OperateurTournee = keyof typeof OPERATEURS_TOURNEE;
export const CODES_OPERATEURS = Object.keys(OPERATEURS_TOURNEE) as [OperateurTournee, ...OperateurTournee[]];

export const ETATS_REALISATION = { realisee: 'Réalisée', partielle: 'Réalisée partiellement', non_realisee: 'Non réalisée' } as const;
export type EtatRealisation = keyof typeof ETATS_REALISATION;
export const CODES_ETATS_REALISATION = Object.keys(ETATS_REALISATION) as [EtatRealisation, ...EtatRealisation[]];

export const NATURES_COUT = { prestation: 'Prestation de collecte', carburant: 'Carburant', entretien: 'Entretien du véhicule', main_oeuvre: 'Main-d\'œuvre', traitement: 'Traitement et décharge', autre: 'Autre' } as const;
export type NatureCout = keyof typeof NATURES_COUT;
export const CODES_NATURES_COUT = Object.keys(NATURES_COUT) as [NatureCout, ...NatureCout[]];

/** Jours de la semaine, norme ISO (1 = lundi ... 7 = dimanche). */
export const JOURS_SEMAINE: Record<number, string> = { 1: 'Lundi', 2: 'Mardi', 3: 'Mercredi', 4: 'Jeudi', 5: 'Vendredi', 6: 'Samedi', 7: 'Dimanche' };

/** Catégorie des signalements qui sont des dépôts sauvages (D-044) ; la table des signalements est posée par la partie A. */
export const CATEGORIE_DEPOT_SAUVAGE = 'depot_sauvage' as const;

export const schemaJourCivil = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date au format AAAA-MM-JJ');
export const schemaHeure = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Heure au format HH:MM');

// Référentiels ------------------------------------------------------------------------------

export const schemaCreationSecteur = z.strictObject({
  /** Absent : généré à partir des initiales du nom (D-038, D-049). */
  code: schemaCodeProprete.optional(),
  nom: z.string().trim().min(2).max(120),
  emprise: schemaPolygoneGeoJson.optional(),
  service_id: uuid.optional(),
  arrondissement_id: uuid.optional(),
});
export const schemaModificationSecteur = z
  .strictObject({ nom: z.string().trim().min(2).max(120), emprise: schemaPolygoneGeoJson.nullable(), service_id: uuid.nullable(), arrondissement_id: uuid.nullable(), actif: z.boolean() })
  .partial()
  .refine((v) => Object.keys(v).length > 0, 'Au moins un champ à modifier');

export const schemaCreationPointCollecte = z.strictObject({
  code: schemaCodeProprete.optional(),
  nom: z.string().trim().min(2).max(120),
  type: z.enum(CODES_TYPES_POINT),
  secteur_id: uuid.optional(),
  position: schemaPosition,
  adresse: z.string().trim().max(300).optional(),
  /** Fréquence de collecte souhaitée, en passages par semaine. */
  frequence_hebdomadaire: z.number().int().min(0).max(14).default(1),
  capacite_litres: z.number().int().positive().max(100_000).optional(),
});
export const schemaModificationPointCollecte = z
  .strictObject({
    nom: z.string().trim().min(2).max(120),
    type: z.enum(CODES_TYPES_POINT),
    secteur_id: uuid.nullable(),
    position: schemaPosition,
    adresse: z.string().trim().max(300).nullable(),
    frequence_hebdomadaire: z.number().int().min(0).max(14),
    capacite_litres: z.number().int().positive().max(100_000).nullable(),
    actif: z.boolean(),
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, 'Au moins un champ à modifier');

export const schemaCreationVehicule = z.strictObject({
  code: schemaCodeProprete.optional(),
  immatriculation: z.string().trim().min(2).max(20),
  type: z.string().trim().min(2).max(80),
  capacite_m3: z.number().positive().max(1000).optional(),
  prestataire_id: uuid.optional(),
});
export const schemaModificationVehicule = z
  .strictObject({ immatriculation: z.string().trim().min(2).max(20), type: z.string().trim().min(2).max(80), capacite_m3: z.number().positive().max(1000).nullable(), prestataire_id: uuid.nullable(), actif: z.boolean() })
  .partial()
  .refine((v) => Object.keys(v).length > 0, 'Au moins un champ à modifier');

// Le schéma de création d'un prestataire est celui de la partie A (interventions.ts,
// schemaCreationPrestataire) : une seule définition dans les contrats depuis la fusion du lot L3.

// Tournées et réalisations --------------------------------------------------------------------

export const schemaCreationTournee = z
  .strictObject({
    code: schemaCodeProprete.optional(),
    nom: z.string().trim().min(2).max(120),
    secteur_id: uuid,
    jour_semaine: z.number().int().min(1).max(7),
    heure: schemaHeure,
    operateur: z.enum(CODES_OPERATEURS).default('commune'),
    equipe_id: uuid.optional(),
    prestataire_id: uuid.optional(),
    vehicule_id: uuid.optional(),
    /** Points de collecte dans l'ordre de passage. */
    points: z.array(uuid).max(500).default([]),
  })
  .refine((v) => v.operateur !== 'prestataire' || Boolean(v.prestataire_id), { message: 'Une tournée confiée à un prestataire désigne ce prestataire', path: ['prestataire_id'] })
  .refine((v) => new Set(v.points).size === v.points.length, { message: 'Un point ne figure qu\'une fois dans la tournée', path: ['points'] });

export const schemaModificationTournee = z
  .strictObject({
    nom: z.string().trim().min(2).max(120),
    secteur_id: uuid,
    jour_semaine: z.number().int().min(1).max(7),
    heure: schemaHeure,
    operateur: z.enum(CODES_OPERATEURS),
    equipe_id: uuid.nullable(),
    prestataire_id: uuid.nullable(),
    vehicule_id: uuid.nullable(),
    points: z.array(uuid).max(500),
    actif: z.boolean(),
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, 'Au moins un champ à modifier')
  .refine((v) => !v.points || new Set(v.points).size === v.points.length, { message: 'Un point ne figure qu\'une fois dans la tournée', path: ['points'] });

/** Réalisation d'une tournée un jour donné : réalisée, partielle (points collectés) ou non réalisée avec motif. */
export const schemaRealisationTournee = z
  .strictObject({
    date: schemaJourCivil,
    etat: z.enum(CODES_ETATS_REALISATION),
    motif: z.string().trim().min(3).max(500).optional(),
    /** Points effectivement collectés (identifiants de points de collecte) ; tous pour une tournée réalisée. */
    points_collectes: z.array(uuid).max(500).default([]),
    observations: z.string().trim().max(2000).optional(),
    debut: z.iso.datetime({ offset: true }).optional(),
    fin: z.iso.datetime({ offset: true }).optional(),
  })
  .refine((v) => v.etat !== 'non_realisee' || Boolean(v.motif), { message: 'Une tournée non réalisée porte un motif', path: ['motif'] })
  .refine((v) => v.etat !== 'partielle' || v.points_collectes.length > 0, { message: 'Une réalisation partielle précise les points collectés', path: ['points_collectes'] });
export type RealisationTournee = z.infer<typeof schemaRealisationTournee>;

export const schemaFiltreTournees = z.strictObject({
  secteur_id: uuid.optional(),
  jour_semaine: z.coerce.number().int().min(1).max(7).optional(),
  operateur: z.enum(CODES_OPERATEURS).optional(),
  actif: z.enum(['oui', 'non']).optional(),
});

export const schemaFiltreRealisations = z.strictObject({
  tournee_id: uuid.optional(),
  etat: z.enum(CODES_ETATS_REALISATION).optional(),
  debut: schemaJourCivil.optional(),
  fin: schemaJourCivil.optional(),
  limite: z.coerce.number().int().min(1).max(500).default(100),
});

/** Calendrier d'une semaine : la semaine du jour donné (lundi au dimanche) ; défaut, semaine courante. */
export const schemaCalendrierSemaine = z.strictObject({ semaine: schemaJourCivil.optional() });

// Coûts ------------------------------------------------------------------------------------

export const schemaCreationCout = z
  .strictObject({
    tournee_id: uuid.optional(),
    prestataire_id: uuid.optional(),
    periode_debut: schemaJourCivil,
    periode_fin: schemaJourCivil,
    nature: z.enum(CODES_NATURES_COUT),
    libelle: texteCourt(200),
    /** Montant en francs CFA, entier. */
    montant: z.number().int().min(0).max(1_000_000_000_000),
    piece_document_id: uuid.optional(),
  })
  .refine((v) => Boolean(v.tournee_id) !== Boolean(v.prestataire_id), 'Un coût se rattache à une tournée ou à un prestataire, pas aux deux')
  .refine((v) => v.periode_fin >= v.periode_debut, { message: 'La fin de la période précède son début', path: ['periode_fin'] });

export const schemaFiltreCouts = z.strictObject({
  tournee_id: uuid.optional(),
  prestataire_id: uuid.optional(),
  nature: z.enum(CODES_NATURES_COUT).optional(),
  debut: schemaJourCivil.optional(),
  fin: schemaJourCivil.optional(),
  limite: z.coerce.number().int().min(1).max(500).default(100),
});

export const schemaFiltreCarteProprete = z.strictObject({ bbox: schemaBbox.optional() });

// Synchronisation terrain (section 4 du cadrage L3) ----------------------------------------

/** Résultat du traitement d'une opération reçue (journal operation_synchronisation). */
export const RESULTATS_OPERATION_SYNCHRONISATION = ['appliquee', 'ignoree_doublon', 'conflit', 'rejetee'] as const;
export type ResultatOperationSynchronisation = (typeof RESULTATS_OPERATION_SYNCHRONISATION)[number];

/** Enveloppe d'une opération : identifiant client, objet visé, version attendue, instant d'enregistrement sur l'appareil, charge. */
export function schemaEnveloppeOperation<T extends string, O extends string, C extends z.ZodTypeAny>(type: T, objetType: O, charge: C) {
  return z.strictObject({
    id_client: uuid,
    type: z.literal(type),
    objet_type: z.literal(objetType),
    objet_id: uuid,
    version_attendue: z.number().int().min(0),
    enregistre_le: z.iso.datetime({ offset: true }),
    charge,
  });
}

/** Opération « realisation_tournee » : réalisation saisie sur l'appareil, visant une tournée et sa version connue. */
export const schemaOperationRealisationTournee = schemaEnveloppeOperation('realisation_tournee', 'tournee', schemaRealisationTournee);
export type OperationRealisationTournee = z.infer<typeof schemaOperationRealisationTournee>;

export const schemaLotOperationsProprete = z.strictObject({ operations: z.array(schemaOperationRealisationTournee).min(1).max(200) });

export interface ResultatOperation {
  id_client: string;
  resultat: ResultatOperationSynchronisation;
  motif?: string;
  version?: number;
  objet_id?: string;
}

/**
 * Tournée emportée sur l'appareil (GET /terrain/travail, page « tournees ») : tournée active d'une équipe
 * de l'agent, prochaine occurrence à partir du jour du téléchargement (jour de Libreville), points
 * ordonnés, version (D-130 : elle n'avance qu'aux modifications du bureau), réalisation connue ce jour-là.
 */
export const schemaTourneeTerrain = z.object({
  id: uuid,
  code: z.string(),
  nom: z.string(),
  secteur: z.object({ id: uuid, nom: z.string() }),
  jour_semaine: z.number().int().min(1).max(7),
  heure: z.string(),
  date: schemaJourCivil,
  operateur: z.enum(CODES_OPERATEURS),
  equipe: z.object({ id: uuid, nom: z.string() }).nullable(),
  vehicule: z.string().nullable(),
  points: z.array(z.object({ id: uuid, code: z.string(), nom: z.string(), type: z.enum(CODES_TYPES_POINT), ordre: z.number().int(), position: schemaPosition.nullable() })),
  version: z.number().int(),
  realisation: z.object({ id: uuid, etat: z.enum(CODES_ETATS_REALISATION), date: schemaJourCivil, origine: z.enum(['bureau', 'synchronisation']) }).nullable(),
});
export type TourneeTerrain = z.infer<typeof schemaTourneeTerrain>;

// Calendrier des tournées (fonctions pures, testées seules) ----------------------------------

const versUtc = (j: string) => new Date(`${j}T00:00:00Z`);
const versJour = (d: Date) => d.toISOString().slice(0, 10);

/** Jour de la semaine ISO d'une date civile (1 = lundi ... 7 = dimanche). */
export function jourSemaineDe(jour: string): number {
  const d = versUtc(jour).getUTCDay();
  return d === 0 ? 7 : d;
}

/** Les sept jours civils de la semaine qui contient le jour donné (lundiDeLaSemaine des rendez-vous). */
export function joursDeLaSemaine(jour: string): string[] {
  const base = versUtc(lundiDeLaSemaine(jour));
  return Array.from({ length: 7 }, (_, i) => versJour(new Date(base.getTime() + i * 86_400_000)));
}

export interface TourneePlanifiee {
  id: string;
  jour_semaine: number;
  heure: string;
  actif: boolean;
}

/** Occurrences des tournées actives sur une semaine : une par tournée et par jour correspondant, triées par jour puis heure. */
export function occurrencesDeLaSemaine<T extends TourneePlanifiee>(tournees: readonly T[], lundi: string): { date: string; tournee: T }[] {
  const jours = joursDeLaSemaine(lundi);
  return jours
    .flatMap((date, i) => tournees.filter((t) => t.actif && t.jour_semaine === i + 1).map((tournee) => ({ date, tournee })))
    .sort((a, b) => a.date.localeCompare(b.date) || a.tournee.heure.localeCompare(b.tournee.heure));
}

/** Date de la prochaine occurrence d'une tournée à partir d'un jour (inclus). */
export function prochaineOccurrence(t: Pick<TourneePlanifiee, 'jour_semaine'>, depuis: string): string {
  const ecart = (t.jour_semaine - jourSemaineDe(depuis) + 7) % 7;
  return versJour(new Date(versUtc(depuis).getTime() + ecart * 86_400_000));
}

// Carte --------------------------------------------------------------------------------------

export interface ProprietesCarteSecteur {
  objet: 'secteur_proprete';
  id: string;
  code: string;
  nom: string;
  tournees: number;
  points: number;
  centre: { longitude: number; latitude: number };
}

export interface ProprietesCartePointCollecte {
  objet: 'point_collecte';
  id: string;
  code: string;
  nom: string;
  type: TypePointCollecte;
  type_libelle: string;
  secteur_id: string | null;
  frequence_hebdomadaire: number;
}
